/**
 * Separate Offers picker (V2 card layout).
 *
 * Self-contained: scoped entirely to its own `[data-separate-picker-offers]` container so it
 * never touches the legacy `_pdp-picker-offers` block. It still emits the exact DOM contract the
 * shared ProductPicker consumes — `.picker-dogsize_offers-group` containers with IDs
 * `picker-dogsize_{index}_offers-group-{sub|otp}` and `.picker-offer_input` radios — so Add-to-Cart
 * keeps working without any change to section-product-main / section-main-product-jc.
 */
class SeparatePickerOffers extends HTMLElement {
  constructor() {
    super();
    this.content = null;
    this.onDogsizeChange = this.onDogsizeChange.bind(this);
    this.onOfferChange = this.onOfferChange.bind(this);
  }

  connectedCallback() {
    this.content = this.querySelector('[data-separate-offers-content]');
    if (!this.content) return;

    this.buildGroups();

    document.addEventListener('dogsize:change', this.onDogsizeChange);
    this.content.addEventListener('change', this.onOfferChange);

    this.selectInitialDogsize();
  }

  disconnectedCallback() {
    document.removeEventListener('dogsize:change', this.onDogsizeChange);
    this.content?.removeEventListener('change', this.onOfferChange);
  }

  /**
   * Group flat offer blocks by dogsize index and produce the sub/otp group containers
   * ProductPicker expects. Offers with dogsize index 0 are always visible.
   */
  buildGroups() {
    const offerBlocks = this.content.querySelectorAll('.picker-offer-block');
    const groups = {};
    const alwaysVisibleOffers = [];

    offerBlocks.forEach((block) => {
      if (block.hasAttribute('data-offer-always-visible')) {
        alwaysVisibleOffers.push(block);
        return;
      }
      const dogsizeIndex = block.dataset.offerDogsize || '1';
      if (!groups[dogsizeIndex]) groups[dogsizeIndex] = [];
      groups[dogsizeIndex].push(block);
    });

    this.content.innerHTML = '';

    if (alwaysVisibleOffers.length > 0) {
      const alwaysVisibleGroup = this.createGroupElement('0', 'sub');
      alwaysVisibleGroup.classList.add('picker-offers__group--always-visible');
      alwaysVisibleGroup.dataset.alwaysVisible = '';
      alwaysVisibleGroup.style.display = 'block';

      const wrapper = alwaysVisibleGroup.querySelector('.picker-offers__group-wrapper');
      alwaysVisibleOffers.forEach((block) => wrapper.appendChild(block));
      this.content.appendChild(alwaysVisibleGroup);

      this.selectDefaultOffer(alwaysVisibleGroup);
    }

    Object.keys(groups)
      .sort()
      .forEach((index) => {
        // Subscription group — holds the real offer blocks
        const subGroup = this.createGroupElement(index, 'sub');
        const subWrapper = subGroup.querySelector('.picker-offers__group-wrapper');
        groups[index].forEach((block) => subWrapper.appendChild(block));
        this.content.appendChild(subGroup);

        // OTP group — clones so ProductPicker can switch purchase type without losing offers
        const otpGroup = this.createGroupElement(index, 'otp');
        otpGroup.classList.add('picker-offers__group--otp');
        const otpWrapper = otpGroup.querySelector('.picker-offers__group-wrapper');
        groups[index].forEach((block) => {
          const clone = block.cloneNode(true);
          const input = clone.querySelector('.picker-offer_input');
          const label = clone.querySelector('label');
          if (input && label) {
            const newId = input.id + '-otp';
            input.id = newId;
            input.name = 'picker-offer-otp-' + index;
            label.setAttribute('for', newId);
          }
          otpWrapper.appendChild(clone);
        });
        this.content.appendChild(otpGroup);
      });
  }

  createGroupElement(index, type) {
    const group = document.createElement('div');
    group.className = 'picker-dogsize_offers-group picker-offers__group';
    group.id = 'picker-dogsize_' + index + '_offers-group-' + type;
    group.dataset.offersGroup = '';
    group.dataset.dogsizeIndex = index;
    group.style.display = 'none';

    const wrapper = document.createElement('div');
    wrapper.className = 'picker-offers__group-wrapper';
    group.appendChild(wrapper);

    return group;
  }

  isVisible(input) {
    const block = input.closest('.picker-offer-block');
    return block && getComputedStyle(block).display !== 'none';
  }

  selectDefaultOffer(group) {
    const allInputs = Array.from(group.querySelectorAll('.picker-offer_input'));
    const allDefaults = Array.from(group.querySelectorAll('[data-is-default="true"]'));
    const defaultOffer = allDefaults.find((i) => this.isVisible(i)) || allDefaults[0] || null;
    const firstOffer = allInputs.find((i) => this.isVisible(i)) || allInputs[0] || null;
    const offerToSelect = defaultOffer || firstOffer;

    if (offerToSelect && !offerToSelect.checked) {
      offerToSelect.checked = true;
      offerToSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  showOffersForDogsize(selectedIndex) {
    if (!selectedIndex || isNaN(selectedIndex)) return;

    const allGroups = this.content.querySelectorAll('[data-offers-group]');

    allGroups.forEach((group) => {
      if (group.hasAttribute('data-always-visible')) return;

      const groupIndex = parseInt(group.dataset.dogsizeIndex, 10);
      const isSubGroup = (group.id || '').endsWith('-sub');

      if (groupIndex === selectedIndex && isSubGroup) {
        group.style.display = 'block';
        this.selectDefaultOffer(group);
      } else {
        group.style.display = 'none';
      }
    });
  }

  /** Keep the sub and otp clones of a group in sync when an offer is picked. */
  syncOfferSelection(selectedGroup, selectedIndex) {
    const allGroups = this.content.querySelectorAll('[data-offers-group]');
    allGroups.forEach((group) => {
      if (group === selectedGroup) return;
      const correspondingRadio = group.querySelectorAll('.picker-offer_input')[selectedIndex];
      if (correspondingRadio) correspondingRadio.checked = true;
    });
  }

  onDogsizeChange(event) {
    this.showOffersForDogsize(event.detail.index);
  }

  onOfferChange(event) {
    if (!event.target.classList.contains('picker-offer_input')) return;
    const group = event.target.closest('[data-offers-group]');
    if (!group) return;
    const inputs = group.querySelectorAll('.picker-offer_input');
    const selectedIndex = Array.from(inputs).indexOf(event.target);
    this.syncOfferSelection(group, selectedIndex);
  }

  selectInitialDogsize() {
    const dogsizesTarget = document.querySelector('[data-block-dogsizes-target]');
    let checkedIndex = null;

    if (dogsizesTarget) {
      const items = dogsizesTarget.querySelectorAll('.picker-dogsize_input, .picker-dogsize_link');
      items.forEach((item, i) => {
        if (item.checked) checkedIndex = i + 1;
      });
    }

    if (checkedIndex) {
      this.showOffersForDogsize(checkedIndex);
    } else {
      const firstGroup = this.content.querySelector('[id$="_offers-group-sub"]');
      if (firstGroup) {
        firstGroup.style.display = 'block';
        this.selectDefaultOffer(firstGroup);
      }
    }
  }
}

if (!customElements.get('separate-picker-offers')) {
  customElements.define('separate-picker-offers', SeparatePickerOffers);
}
